/**
 * Formatting for engine metrics. The catalog names a format per metric
 * (`count` | `usd` | `usd_unit` | `pct` | `ratio` | `timestamp`); decimals come from the catalog's `conventions.format`
 * when it sends them, else the spec's defaults. Locale-aware through i18n/format.
 *
 * `pct` values arrive on the catalog's `conventions.format.pct.scale` (metrics.yaml: 100,
 * i.e. 12.3 means 12.3%); a scale of 1 means fractions. Pages never format or derive
 * these numbers themselves.
 */
import {
  formatCompact, formatCurrency, formatDateTime, formatNumber, formatPercent,
} from '../i18n/format';

export const NO_VALUE = '—';

/** Numbers from here on show compact (1.2K, 3.4M, 1.1B), every metric on every page (decided
 *  2026-10-17); the exact figure stays on hover (MetricValue's title). */
export const COMPACT_FROM = 1000;
export const APPROX = '≈';

const DEFAULT_DECIMALS = { count: 0, usd: 2, pct: 1, usd_unit: 4, ratio: 2 };

const decimalsFor = (format, conventions) => {
  const conv = conventions?.format?.[format];
  if (conv && typeof conv === 'object' && Number.isFinite(conv.decimals)) return conv.decimals;
  return DEFAULT_DECIMALS[format] ?? 0;
};

/** What 100% is in the engine's pct values (metrics.yaml `pct: {scale: 100}`). */
const pctScale = (conventions) => {
  const s = Number(conventions?.format?.pct?.scale);
  return Number.isFinite(s) && s > 0 ? s : 100;
};

const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/**
 * One value as display text, or null when there is no value (the caller renders "—";
 * 0 is a real zero and formats as one).
 */
export function formatMetricValue(value, format, locale, { compact = true, conventions } = {}) {
  if (!isNum(value)) return null;
  const n = Number(value);
  // a moment (a last_seen metric): epoch milliseconds, shown as a date and time
  if (format === 'timestamp') return formatDateTime(n, locale);
  const d = decimalsFor(format, conventions);
  if (format === 'usd_unit') {
    // a small unit price: up to the catalog's decimals (4), no fewer than 2
    return formatCurrency(n, locale, 'USD', { minimumFractionDigits: Math.min(2, d), maximumFractionDigits: d });
  }
  if (format === 'usd') {
    return compact && Math.abs(n) >= COMPACT_FROM
      ? formatCurrency(n, locale, 'USD', { notation: 'compact', maximumFractionDigits: 1 })
      : formatCurrency(n, locale, 'USD', { minimumFractionDigits: d, maximumFractionDigits: d });
  }
  if (format === 'pct') {
    return formatPercent(n / pctScale(conventions), locale, { minimumFractionDigits: 0, maximumFractionDigits: d });
  }
  if (compact && Math.abs(n) >= COMPACT_FROM) return formatCompact(n, locale);
  return formatNumber(n, locale, { maximumFractionDigits: d });
}

/**
 * The change vs the previous period, LineArea-style: "+301 (from 3)", "−12 (from 40)",
 * "±0". A percentage off a tiny base reads as noise, so counts and money show the
 * base instead. Percentages change in points: "+2.5 pt". Null when the engine sent
 * no comparison — never computed here.
 */
export function formatMetricDelta(compare, format, locale, t, { conventions } = {}) {
  if (!compare || !isNum(compare.delta)) return null;
  const dv = Number(compare.delta);
  const sign = dv > 0 ? '+' : dv < 0 ? '−' : '±';
  if (format === 'pct') {
    const pts = formatNumber((Math.abs(dv) * 100) / pctScale(conventions), locale, {
      maximumFractionDigits: decimalsFor('pct', conventions),
    });
    return `${sign}${t('metrics:delta.points', { n: pts })}`;
  }
  const abs = formatMetricValue(Math.abs(dv), format, locale, { conventions });
  const prev = isNum(compare.previous) ? formatMetricValue(compare.previous, format, locale, { conventions }) : null;
  const rel = dv !== 0 && prev != null ? ` (${t('metrics:delta.from', { n: prev })})` : '';
  return `${sign}${abs}${rel}`;
}

/**
 * The engine's relative change (`change_pct`, in percent) as "+12.3%", "−40%", "±0%". Null
 * when the engine sent none (no baseline) — never computed here.
 */
export function formatMetricChange(changePct, locale, { conventions } = {}) {
  if (!isNum(changePct)) return null;
  const n = Number(changePct);
  const sign = n > 0 ? '+' : n < 0 ? '−' : '±';
  return `${sign}${formatMetricValue(Math.abs(n), 'pct', locale, { conventions })}`;
}
